import type { LucideIcon } from "@/components/icons"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

type AdminSubNavigationOption = {
  id: string
  icon?: LucideIcon
  label: string
}

type AdminSubNavigationProps = {
  ariaLabel: string
  mobileLabel: string
  options: AdminSubNavigationOption[]
  value: string
  fillAvailableWidth?: boolean
  mobileTestId?: string
  navigationTestId?: string
  onValueChange: (value: string) => void
}

export function AdminSubNavigation({
  ariaLabel,
  mobileLabel,
  options,
  value,
  fillAvailableWidth = false,
  mobileTestId,
  navigationTestId,
  onValueChange,
}: AdminSubNavigationProps) {
  return (
    <>
      <div className="lg:hidden">
        <label className="text-muted-foreground mb-1.5 block text-xs font-semibold tracking-wider uppercase">
          {mobileLabel}
        </label>
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger
            aria-label={ariaLabel}
            className="min-h-11 w-full rounded-md"
            data-testid={mobileTestId}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => {
              const Icon = option.icon
              return (
                <SelectItem key={option.id} value={option.id}>
                  <span className="flex items-center gap-2">
                    {Icon ? <Icon aria-hidden="true" className="size-4" /> : null}
                    {option.label}
                  </span>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>

      <nav
        aria-label={ariaLabel}
        data-testid={navigationTestId}
        className={cn(
          "border-border hidden min-w-0 border-b lg:flex",
          fillAvailableWidth ? "" : "gap-1 overflow-x-auto"
        )}
      >
        {options.map((option) => {
          const Icon = option.icon
          const isActive = option.id === value
          return (
            <button
              key={option.id}
              aria-current={isActive ? "page" : undefined}
              type="button"
              className={cn(
                "text-muted-foreground relative flex min-h-11 items-center gap-2 py-2.5 text-sm font-medium transition-colors",
                "hover:text-foreground focus-visible:ring-ring focus-visible:z-10 focus-visible:ring-2 focus-visible:outline-none",
                fillAvailableWidth ? "flex-1 justify-center px-3" : "shrink-0 px-4",
                isActive &&
                  (fillAvailableWidth ? "text-foreground" : "text-primary dark:text-foreground")
              )}
              onClick={() => {
                if (!isActive) onValueChange(option.id)
              }}
            >
              {Icon ? <Icon aria-hidden="true" className="size-4 shrink-0" /> : null}
              <span>{option.label}</span>
              {isActive ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    "bg-primary absolute -bottom-px h-0.5 rounded-full",
                    fillAvailableWidth ? "inset-x-3" : "inset-x-2"
                  )}
                />
              ) : null}
            </button>
          )
        })}
      </nav>
    </>
  )
}
